import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import VSAIWidget from "@/components/VSAIWidget";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import {
  ArrowRight, MapPin, Briefcase, Search, Cpu, Database, Cloud as CloudIcon, LineChart, Bug, Layers,
  Brain, Zap, Code2, GraduationCap, Rocket, Sparkles, Building2, CalendarDays, Trophy, HeartHandshake
} from "lucide-react";

const TECH = [
  { key: "Java",           desc: "Enterprise backends, distributed systems and Spring Boot microservices for global clients.",           skills: ["Java", "Spring Boot", "SQL", "Microservices", "REST APIs"] },
  { key: "Python",         desc: "APIs, automation, data pipelines and AI systems powered by FastAPI and modern Python.",                skills: ["Python", "FastAPI", "SQL", "REST APIs", "AsyncIO"] },
  { key: "AI/ML",          desc: "LLMs, applied ML, MLOps and computer vision — from research to production.",                            skills: ["Python", "PyTorch", "ML", "LLMs", "MLOps"] },
  { key: "Data Analytics", desc: "Turn business questions into dashboards, insights and decisions at scale.",                             skills: ["SQL", "Python", "Tableau", "Excel", "Statistics"] },
  { key: "Data Science",   desc: "Modelling, experimentation and production-grade data products for enterprise workflows.",              skills: ["Python", "Statistics", "ML", "SQL", "Notebooks"] },
  { key: "MERN",           desc: "Modern web applications built with MongoDB, Express, React and Node — end-to-end.",                    skills: ["React", "Node.js", "Express", "MongoDB", "TypeScript"] },
  { key: "DevOps",         desc: "CI/CD, observability, infra automation and reliability engineering across cloud estates.",             skills: ["Docker", "Kubernetes", "AWS", "Terraform", "CI/CD"] },
  { key: "Cloud",          desc: "AWS, GCP and Azure architectures — designed for scale, security and cost efficiency.",                 skills: ["AWS", "GCP", "Azure", "Kubernetes", "IaC"] },
  { key: "QA/Testing",     desc: "Automation-first testing, performance, and end-to-end quality ownership across products.",             skills: ["Selenium", "Playwright", "Python", "SQL", "API Testing"] },
];

const JOURNEY = [
  { s: "Discover", d: "Explore live roles that match your ambitions." },
  { s: "Apply", d: "One tap, guided by your profile." },
  { s: "Assess", d: "A skill-first, job-specific assessment." },
  { s: "Interview", d: "Meet the team building the future." },
  { s: "Offer", d: "Transparent, competitive, on time." },
  { s: "Onboard", d: "Set up for day one and beyond." },
  { s: "Grow", d: "Careers designed for the long run." },
];

const LIFE_IMAGES = [
  { alt: "Engineering team", src: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", label: "People" },
  { alt: "Modern office", src: "https://images.pexels.com/photos/358549/pexels-photo-358549.jpeg", label: "Culture" },
  { alt: "Whiteboarding", src: "https://images.unsplash.com/photo-1552664730-d307ca884978?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", label: "Collaboration" },
  { alt: "Learning", src: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", label: "Learning" },
];

const LOCATION_IMAGES = {
  "Hyderabad": "https://images.unsplash.com/photo-1596436831045-e4c66fb2b3b1?w=800&q=80",
  "Bengaluru": "https://images.unsplash.com/photo-1580655653885-65763b2597d0?w=800&q=80",
  "Chennai": "https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=800&q=80",
  "Pune": "https://images.unsplash.com/photo-1567443024551-f3e3cc2be870?w=800&q=80",
  "Mumbai": "https://images.unsplash.com/photo-1529253355930-ddbe423a2ac7?w=800&q=80",
  "Delhi NCR": "https://images.unsplash.com/photo-1587474260584-136574528ed5?w=800&q=80",
  "Remote": "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&q=80",
};

const TECH_ICONS = {
  Code2: "Code2", Cpu: "Cpu", Brain: "Brain", LineChart: "LineChart",
  Database: "Database", Layers: "Layers", Zap: "Zap", Cloud: "Cloud", Bug: "Bug",
};

function TechnologyShowcase({ techs }) {
  const list = techs || [];
  const [active, setActive] = React.useState(null);
  React.useEffect(() => { if (list.length && !active) setActive(list[0].key); }, [list, active]);
  // Counts are authoritative from the backend (/api/career-domains) — computed
  // DB-side across ALL published jobs, so no client-side pagination bug.
  const countFor = (t) => (typeof t.active_jobs === "number" ? t.active_jobs : 0);
  const activeTech = list.find(t => t.key === active) || list[0];
  if (!activeTech) return <p className="text-slate-500 mt-8">Loading technologies...</p>;
  const cnt = countFor(activeTech);
  return (
    <div className="mt-14 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-7">
        <div className="relative border border-slate-200 rounded-2xl p-8 md:p-10 bg-white">
          <div className="absolute top-6 right-6 text-[10px] uppercase tracking-[0.25em] text-slate-400 font-semibold">Featured</div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold">Technology</div>
          <h3 className="mt-2 font-display text-4xl md:text-5xl font-bold text-[#0A2540] leading-[1.05]" data-testid="tech-featured-title">{activeTech.key}</h3>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-xl">{activeTech.description}</p>
          <div className="mt-8 grid grid-cols-2 gap-8">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold">Live openings</div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display text-5xl font-bold text-[#0A2540]" data-testid="tech-featured-count">{cnt}</span>
                <span className="text-sm text-slate-500">{cnt === 1 ? "role" : "roles"}</span>
              </div>
              {cnt === 0 && <div className="mt-1 text-xs text-slate-500">No current opportunities</div>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold">Core skills</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(activeTech.core_skills || []).slice(0,5).map(s => (
                  <span key={s} className="text-xs text-[#0A2540] border border-slate-200 rounded-md px-2 py-0.5 bg-slate-50">{s}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-8 h-px bg-slate-100"/>
          <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
            <Link to={`/jobs?domain=${encodeURIComponent(activeTech.key)}`} data-testid="tech-featured-cta"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A2540] hover:text-[#2563EB] transition-colors">
              Explore Opportunities <ArrowRight className="w-4 h-4"/>
            </Link>
            <span className="text-[11px] text-slate-400">Job counts update in real time from VS-Solutions Careers.</span>
          </div>
        </div>
      </div>
      <div className="lg:col-span-5">
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold mb-4">All technologies</div>
        <div className="border-t border-slate-200">
          {list.map(t => {
            const c = countFor(t);
            const isActive = t.key === activeTech.key;
            return (
              <button key={t.id || t.key} onClick={() => setActive(t.key)} onMouseEnter={() => setActive(t.key)} data-testid={`tech-item-${t.key}`}
                className={`w-full text-left border-b border-slate-200 py-4 flex items-center justify-between gap-4 group transition-colors ${isActive ? "bg-slate-50/70" : "hover:bg-slate-50/50"}`}>
                <div className="flex items-center gap-4 min-w-0">
                  <span className={`w-1 h-6 rounded-full transition-colors ${isActive ? "bg-[#F59E0B]" : "bg-transparent group-hover:bg-slate-200"}`}/>
                  <span className={`font-display text-lg font-semibold truncate ${isActive ? "text-[#0A2540]" : "text-slate-700 group-hover:text-[#0A2540]"}`}>{t.key}</span>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <span className="text-xs tabular-nums text-slate-500">{c} {c === 1 ? "role" : "roles"}</span>
                  <ArrowRight className={`w-4 h-4 transition-all ${isActive ? "text-[#0A2540] translate-x-0" : "text-slate-300 group-hover:text-[#0A2540] group-hover:translate-x-0.5"}`}/>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}


export default function PublicHome() {
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [techs, setTechs] = useState([]);
  const [hackathons, setHackathons] = useState([]);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState("");

  useEffect(() => {
    api.get("/jobs?limit=30").then(r => setJobs(r.data)).catch(()=>{});
    api.get("/locations").then(r => setLocations(r.data)).catch(()=>{});
    api.get("/career-updates?limit=6").then(r => setUpdates(r.data)).catch(()=>{});
    api.get("/career-domains").then(r => setTechs(r.data)).catch(()=>{});
    api.get("/hackathons?limit=3").then(r => setHackathons(r.data)).catch(()=>{});
  }, []);

  const filtered = jobs.filter(j => {
    if (tab === "freshers") return j.experience_category === "Fresher" || (j.experience_min||0) === 0;
    if (tab === "experienced") return j.experience_category !== "Fresher" && (j.experience_min||0) > 0;
    if (tab === "all") return true;
    return j.domain === tab;
  }).slice(0, 4);

  const doSearch = (e) => {
    e?.preventDefault();
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (loc) p.set("location", loc);
    nav(`/jobs?${p.toString()}`);
  };

  const featured = updates.find(u => u.featured) || updates[0];
  const supporting = updates.filter(u => u.id !== featured?.id).slice(0, 2);

  // location -> live job count
  const locJobCount = (name) => jobs.filter(j => (j.location||"").toLowerCase() === name.toLowerCase()).length;
  const topLocations = [...(locations || [])].sort((a,b) => locJobCount(b.name) - locJobCount(a.name)).slice(0, 6);

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      <PublicHeader />

      {/* ─── HERO: split image + text ────────────────────────────────── */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 md:pt-24 md:pb-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 relative z-10 animate-fade-up">
            <Badge className="bg-[#0A2540]/5 text-[#0A2540] border-[#0A2540]/10 hover:bg-[#0A2540]/5">
              <Sparkles className="w-3 h-3 mr-1" /> VS-Solutions Careers 2026
            </Badge>
            <h1 className="mt-6 font-display text-5xl md:text-7xl font-bold leading-[1.02] tracking-tight text-[#0A2540]">
              Build Your Future
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F59E0B]">With VS-Solutions</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Live opportunities across Java, Python, AI/ML, Data, Cloud, DevOps and MERN — for freshers and experienced engineers.
            </p>
            <form onSubmit={doSearch} className="mt-8 bg-white rounded-2xl p-2 flex flex-col md:flex-row gap-2 shadow-[0_20px_60px_-20px_rgba(10,37,64,0.25)] border border-slate-200">
              <div className="flex-1 min-w-0 flex items-center gap-2 px-3">
                <Search className="w-4 h-4 text-slate-400 shrink-0"/>
                <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Job title, skill or technology" className="border-0 focus-visible:ring-0 shadow-none text-slate-900" data-testid="hero-search-q"/>
              </div>
              <div className="hidden md:flex items-center gap-2 px-3 border-l border-slate-200">
                <MapPin className="w-4 h-4 text-slate-400 shrink-0"/>
                <Input value={loc} onChange={e=>setLoc(e.target.value)} placeholder="Location" className="border-0 focus-visible:ring-0 shadow-none text-slate-900 w-40" data-testid="hero-search-loc"/>
              </div>
              <Button type="submit" className="bg-[#0A2540] hover:bg-[#0A2540]/90 font-semibold h-11" data-testid="hero-search-submit">
                Search Jobs
              </Button>
            </form>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-[#0A2540] font-semibold" data-testid="hero-explore">
                <Link to="/jobs">Explore Opportunities <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              <Button asChild size="lg" variant="ghost" className="text-[#0A2540] hover:bg-slate-100" data-testid="hero-talent">
                <Link to="/register">Join Our Talent Community</Link>
              </Button>
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#2563EB]/20 via-[#F59E0B]/10 to-transparent rounded-3xl blur-2xl"/>
              <div className="relative rounded-3xl overflow-hidden aspect-[4/5] md:aspect-[5/6]">
                <img alt="Engineering team at VS-Solutions" src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&q=85" className="w-full h-full object-cover"/>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/70 via-transparent to-transparent"/>
                <div className="absolute bottom-6 left-6 right-6 grid grid-cols-3 gap-3">
                  {[
                    { k: `${jobs.length}+`, v: "Live Roles" },
                    { k: "9", v: "Domains" },
                    { k: "12", v: "Locations" },
                  ].map((s,i)=>(
                    <div key={i} className="backdrop-blur bg-white/10 border border-white/20 rounded-lg p-3 text-white">
                      <div className="font-display text-2xl font-bold">{s.k}</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-200 mt-0.5">{s.v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── EXPLORE WHAT'S NEXT ─────────────────────────────────────── */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold">Featured Roles</div>
              <h2 className="mt-3 font-display text-4xl md:text-6xl font-bold text-[#0A2540] leading-[1.05]">Explore what's next</h2>
              <p className="text-slate-600 mt-3 max-w-xl">Handpicked live opportunities across VS-Solutions engineering, data and cloud teams.</p>
            </div>
            <Button variant="outline" asChild className="w-fit" data-testid="live-view-all"><Link to="/jobs">View All Opportunities <ArrowRight className="w-4 h-4 ml-1"/></Link></Button>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="mb-8">
            <TabsList className="flex-wrap h-auto justify-start bg-white border border-slate-200 p-1">
              <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
              <TabsTrigger value="freshers" data-testid="tab-freshers">Freshers</TabsTrigger>
              <TabsTrigger value="experienced" data-testid="tab-experienced">Experienced</TabsTrigger>
              {TECH.slice(0,6).map(t => <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`}>{t.key}</TabsTrigger>)}
            </TabsList>
          </Tabs>          <div className="grid md:grid-cols-2 gap-4">
            {filtered.map(j => (
              <Link key={j.id} to={`/jobs/${j.id}`} data-testid={`home-job-${j.id}`} className="group">
                <div className="relative bg-white border border-slate-200 rounded-2xl p-6 md:p-7 card-hover overflow-hidden">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-[#2563EB]/8 to-transparent rounded-full blur-2xl -translate-y-10 translate-x-10"/>
                  <div className="relative flex items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap gap-2">
                        <Badge className="bg-[#0A2540] text-white border-none hover:bg-[#0A2540] text-[10px]">{j.domain || j.department}</Badge>
                        <Badge variant="outline" className="text-[10px]">{j.experience_category || `${j.experience_min}-${j.experience_max} yrs`}</Badge>
                      </div>
                      <h3 className="font-display font-semibold text-xl md:text-2xl mt-3 text-[#0A2540] leading-tight">{j.title}</h3>
                      <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-600">
                        <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5"/>{j.location}</span>
                        <span className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5"/>{j.employment_type}</span>
                        <span>{j.openings} opening{j.openings!==1?"s":""}</span>
                      </div>
                    </div>
                  </div>
                  <div className="relative mt-5 flex flex-wrap gap-1.5">
                    {(j.required_skills||[]).slice(0,4).map(s=><Badge key={s} className="bg-blue-50 text-blue-700 border-none hover:bg-blue-50 text-[11px]">{s}</Badge>)}
                  </div>
                  <div className="relative mt-6 flex items-center justify-between text-sm">
                    <span className="text-slate-400 font-mono text-[11px]">{j.id}</span>
                    <span className="text-[#2563EB] font-semibold group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">View role <ArrowRight className="w-3.5 h-3.5"/></span>
                  </div>
                </div>
              </Link>
            ))}
            {filtered.length === 0 && (
              <div className="md:col-span-2 text-center py-16 text-slate-500 border border-dashed border-slate-300 rounded-2xl">
                <Briefcase className="w-8 h-8 mx-auto mb-2 text-slate-300"/>
                No opportunities in this category yet. <Link to="/jobs" className="text-[#2563EB] underline ml-1">Browse all</Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─── EXPLORE WHERE YOUR SKILLS CAN TAKE YOU (Editorial) ────── */}
      <section className="bg-white py-24 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold">Technologies</div>
            <h2 className="mt-3 font-display text-4xl md:text-6xl font-bold text-[#0A2540] leading-[1.05]">Explore where your skills can take you</h2>
            <p className="mt-4 text-slate-600 text-lg leading-relaxed">Discover opportunities across the technologies powering modern businesses.</p>
          </div>

          <TechnologyShowcase techs={techs}/>
        </div>
      </section>

      {/* ─── FRESHERS & EXPERIENCED PANELS ───────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid md:grid-cols-2 gap-6">
          <Link to="/jobs?experience_category=Fresher" data-testid="path-fresher" className="group relative rounded-3xl overflow-hidden aspect-[5/4] md:aspect-[4/3]">
            <img alt="Students starting careers at VS-Solutions" src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1200&q=85" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540] via-[#0A2540]/60 to-transparent"/>
            <div className="relative h-full flex flex-col justify-end p-8 md:p-10 text-white">
              <GraduationCap className="w-8 h-8 text-[#F59E0B]"/>
              <div className="text-xs uppercase tracking-[0.25em] text-[#F59E0B] font-semibold mt-4">Start Your Career</div>
              <h3 className="font-display text-3xl md:text-4xl font-bold mt-2">Students & Graduates</h3>
              <p className="mt-2 text-slate-200 max-w-md">Freshers, campus hiring, structured mentorship and skill-first assessments.</p>
              <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold">Explore Fresher Opportunities <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform"/></div>
            </div>
          </Link>
          <Link to="/jobs?experience_category=3-5%20Years" data-testid="path-experienced" className="group relative rounded-3xl overflow-hidden aspect-[5/4] md:aspect-[4/3]">
            <img alt="Experienced engineers at VS-Solutions" src="https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?w=1200&q=85" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540] via-[#0A2540]/60 to-transparent"/>
            <div className="relative h-full flex flex-col justify-end p-8 md:p-10 text-white">
              <Rocket className="w-8 h-8 text-[#F59E0B]"/>
              <div className="text-xs uppercase tracking-[0.25em] text-[#F59E0B] font-semibold mt-4">Take Your Career Further</div>
              <h3 className="font-display text-3xl md:text-4xl font-bold mt-2">Experienced Professionals</h3>
              <p className="mt-2 text-slate-200 max-w-md">Own the architecture, mentor teams, grow into technical leadership.</p>
              <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold">Explore Experienced Opportunities <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform"/></div>
            </div>
          </Link>
        </div>
      </section>

      {/* ─── MORE THAN A JOB (FULL-WIDTH EDITORIAL) ─────────────────── */}
      <section className="relative bg-[#0A2540] text-white py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{backgroundImage: "url(https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?w=1600&q=85)", backgroundSize: 'cover', backgroundPosition: 'center'}}/>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540] via-[#0A2540]/95 to-[#0A2540]/60"/>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.25em] text-[#F59E0B] font-semibold">Why VS-Solutions</div>
            <h2 className="mt-3 font-display text-4xl md:text-6xl font-bold leading-[1.05] max-w-2xl">More than a job.<br/>A journey.</h2>
            <p className="mt-6 text-slate-300 max-w-xl text-lg leading-relaxed">
              We invest in the craft, the person, and the outcome. Real client work, modern tooling, generous benefits, and a culture that treats depth as a first-class value.
            </p>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-5">
              {["Technology","Learning","Innovation","Growth","Collaboration"].map(x => (
                <div key={x} className="border-t-2 border-[#F59E0B] pt-3">
                  <div className="font-display text-lg font-semibold">{x}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── CAREER JOURNEY ──────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold">Your Career Journey</div>
        <h2 className="mt-3 font-display text-4xl md:text-6xl font-bold text-[#0A2540] leading-[1.05]">A journey, not a job posting.</h2>
        <div className="mt-14 relative">
          <div className="hidden md:block absolute top-8 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent"/>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
            {JOURNEY.map((step, i) => (
              <div key={step.s} className="relative">
                <div className="w-14 h-14 rounded-full bg-white border-2 border-[#0A2540] flex items-center justify-center relative z-10 shadow-sm">
                  <span className="font-display text-lg font-bold text-[#0A2540]">{i+1}</span>
                </div>
                <div className="mt-4 font-display font-semibold text-[#0A2540]">{step.s}</div>
                <div className="text-xs text-slate-500 mt-1 leading-relaxed">{step.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── LIFE AT VS-SOLUTIONS (COLLAGE) ─────────────────────────── */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-4">
              <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold">Life at VS-Solutions</div>
              <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold text-[#0A2540] leading-[1.05]">People-first.<br/>Engineering-obsessed.</h2>
              <p className="mt-5 text-slate-600 leading-relaxed">
                From how we onboard to how we grow — every ritual is designed to help engineers do the best work of their careers.
              </p>
              <Button asChild variant="outline" className="mt-6"><Link to="/#life">Explore Culture <ArrowRight className="w-4 h-4 ml-2"/></Link></Button>
            </div>
            <div className="lg:col-span-8 grid grid-cols-3 grid-rows-2 gap-3 h-[420px] md:h-[520px]">
              <div className="relative rounded-2xl overflow-hidden col-span-2 row-span-2">
                <img alt={LIFE_IMAGES[0].alt} src={LIFE_IMAGES[0].src} className="absolute inset-0 w-full h-full object-cover"/>
                <div className="absolute bottom-4 left-4 backdrop-blur bg-white/80 rounded-md px-3 py-1 text-xs font-semibold text-[#0A2540]">{LIFE_IMAGES[0].label}</div>
              </div>
              <div className="relative rounded-2xl overflow-hidden">
                <img alt={LIFE_IMAGES[1].alt} src={LIFE_IMAGES[1].src} className="absolute inset-0 w-full h-full object-cover"/>
                <div className="absolute bottom-3 left-3 backdrop-blur bg-white/80 rounded-md px-2 py-0.5 text-[10px] font-semibold text-[#0A2540]">{LIFE_IMAGES[1].label}</div>
              </div>
              <div className="relative rounded-2xl overflow-hidden">
                <img alt={LIFE_IMAGES[2].alt} src={LIFE_IMAGES[2].src} className="absolute inset-0 w-full h-full object-cover"/>
                <div className="absolute bottom-3 left-3 backdrop-blur bg-white/80 rounded-md px-2 py-0.5 text-[10px] font-semibold text-[#0A2540]">{LIFE_IMAGES[2].label}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── LATEST CAREER UPDATES (EDITORIAL) ──────────────────────── */}
      {featured && (
        <section id="updates" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold">Latest From VS-Solutions</div>
              <h2 className="mt-3 font-display text-4xl md:text-6xl font-bold text-[#0A2540] leading-[1.05]">What's new</h2>
            </div>
          </div>
          <div className="grid lg:grid-cols-5 gap-6">
            {/* Featured big */}
            <Link to={featured.cta_url || "/jobs"} className="lg:col-span-3 group" data-testid="update-featured">
              <article className="border border-slate-200 rounded-3xl overflow-hidden bg-white card-hover">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img alt={featured.title} src={featured.image_url || LIFE_IMAGES[0].src} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/40 via-transparent to-transparent"/>
                  {featured.featured && <Badge className="absolute top-4 left-4 bg-[#F59E0B] text-[#0A2540] border-none hover:bg-[#F59E0B]"><Sparkles className="w-3 h-3 mr-1"/>Featured</Badge>}
                </div>
                <div className="p-8">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#2563EB] font-semibold">{featured.category}</div>
                  <h3 className="mt-3 font-display text-2xl md:text-3xl font-bold text-[#0A2540] leading-tight">{featured.title}</h3>
                  <p className="mt-3 text-slate-600 line-clamp-2">{featured.description}</p>
                  <div className="mt-5 flex items-center justify-between text-sm">
                    <span className="text-slate-400">{new Date(featured.created_at).toLocaleDateString(undefined,{month:"short",day:"numeric",year:"numeric"})}</span>
                    <span className="text-[#2563EB] font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">{featured.cta_text || "Read more"} <ArrowRight className="w-3.5 h-3.5"/></span>
                  </div>
                </div>
              </article>
            </Link>
            <div className="lg:col-span-2 space-y-4">
              {supporting.map(u => (
                <Link key={u.id} to={u.cta_url || "/jobs"} className="group block" data-testid={`update-${u.id}`}>
                  <article className="border border-slate-200 rounded-2xl overflow-hidden bg-white card-hover flex flex-col md:flex-row h-full">
                    <div className="relative md:w-2/5 aspect-video md:aspect-auto overflow-hidden">
                      <img alt={u.title} src={u.image_url || LIFE_IMAGES[1].src} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                    </div>
                    <div className="p-5 md:w-3/5">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#2563EB] font-semibold">{u.category}</div>
                      <h4 className="mt-2 font-display text-base font-bold text-[#0A2540] leading-tight line-clamp-2">{u.title}</h4>
                      <div className="mt-3 text-[11px] text-slate-400">{new Date(u.created_at).toLocaleDateString(undefined,{month:"short",day:"numeric"})}</div>
                      <div className="mt-2 text-xs text-[#2563EB] font-semibold flex items-center gap-1">{u.cta_text || "Explore"} <ArrowRight className="w-3 h-3"/></div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── HACKATHONS + EVENTS ─────────────────────────────────────── */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold">Hackathons</div>
              <h2 className="mt-3 font-display text-4xl md:text-6xl font-bold text-[#0A2540] leading-[1.05]">Build. Compete. Get hired.</h2>
            </div>
            {hackathons.length > 0 && <Button variant="outline" asChild><Link to="/hackathons">View all <ArrowRight className="w-4 h-4 ml-1"/></Link></Button>}
          </div>
          {hackathons.length === 0 ? (
            <Card className="p-10 border-dashed border-slate-300 text-center bg-white" data-testid="hackathons-empty">
              <Trophy className="w-10 h-10 mx-auto text-slate-300"/>
              <p className="mt-3 text-slate-500">No upcoming hackathons at the moment.</p>
            </Card>
          ) : (
            <div className="grid lg:grid-cols-3 gap-6">
              {hackathons[0] && (
                <Link to={`/hackathons/${hackathons[0].id}`} className="lg:col-span-2 group" data-testid={`hackathon-featured-${hackathons[0].id}`}>
                  <div className="relative rounded-3xl overflow-hidden aspect-[16/9] md:aspect-[2/1]">
                    <img alt={hackathons[0].name} src={hackathons[0].banner_url || "https://images.unsplash.com/photo-1531482615713-2afd69097998?w=1400&q=85"} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540] via-[#0A2540]/60 to-transparent"/>
                    <div className="relative h-full flex flex-col justify-end p-8 md:p-10 text-white">
                      <Trophy className="w-9 h-9 text-[#F59E0B]"/>
                      <div className="text-xs uppercase tracking-[0.25em] text-[#F59E0B] font-semibold mt-3">{hackathons[0].theme || "Hackathon"}</div>
                      <h3 className="font-display text-3xl md:text-4xl font-bold mt-2">{hackathons[0].name}</h3>
                      <p className="mt-2 text-slate-200 max-w-lg line-clamp-2">{hackathons[0].short_description}</p>
                      <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-200">
                        {hackathons[0].event_start && <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4"/>{hackathons[0].event_start}</span>}
                        {hackathons[0].mode && <span>{hackathons[0].mode}</span>}
                        {hackathons[0].location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4"/>{hackathons[0].location}</span>}
                      </div>
                      <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#F59E0B]">Register Now <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform"/></div>
                    </div>
                  </div>
                </Link>
              )}
              <div className="space-y-4">
                {hackathons.slice(1, 3).map(h => (
                  <Link key={h.id} to={`/hackathons/${h.id}`} className="group block" data-testid={`hackathon-supp-${h.id}`}>
                    <div className="relative rounded-2xl overflow-hidden aspect-video">
                      <img alt={h.name} src={h.banner_url || "https://images.unsplash.com/photo-1531482615713-2afd69097998?w=1000&q=85"} className="absolute inset-0 w-full h-full object-cover"/>
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540] via-[#0A2540]/50 to-transparent"/>
                      <div className="relative h-full flex flex-col justify-end p-5 text-white">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#F59E0B] font-semibold">{h.theme || "Hackathon"}</div>
                        <div className="font-display text-lg font-semibold mt-1 line-clamp-2">{h.name}</div>
                      </div>
                    </div>
                  </Link>
                ))}
                {hackathons.length < 2 && (
                  <Card className="p-6 border-slate-200 bg-white h-full flex items-center justify-center text-center">
                    <div>
                      <CalendarDays className="w-8 h-8 mx-auto text-slate-300"/>
                      <p className="mt-2 text-sm text-slate-500">More events coming soon</p>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ─── LOCATIONS VISUAL ────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold">Locations</div>
        <h2 className="mt-3 font-display text-4xl md:text-6xl font-bold text-[#0A2540] leading-[1.05]">Where you can build your career</h2>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4">
          {topLocations.map((l, i) => {
            const cnt = locJobCount(l.name);
            return (
              <Link key={l.id} to={`/jobs?location=${encodeURIComponent(l.name)}`} data-testid={`loc-${l.name}`}
                className="group relative rounded-2xl overflow-hidden aspect-[4/3] card-hover">
                <img alt={l.name} src={LOCATION_IMAGES[l.name] || LOCATION_IMAGES.Remote} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540] via-[#0A2540]/30 to-transparent"/>
                <div className="relative h-full flex flex-col justify-end p-5 text-white">
                  <div className="font-display text-2xl font-bold">{l.name}</div>
                  <div className="mt-1 text-sm text-slate-200 flex items-center gap-2"><Building2 className="w-3.5 h-3.5"/>{cnt} open role{cnt!==1?"s":""}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#F59E0B] group-hover:gap-2 transition-all">Explore <ArrowRight className="w-3.5 h-3.5"/></div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ─── MEET VS AI ──────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold">Meet VS AI</div>
            <h2 className="mt-3 font-display text-4xl md:text-6xl font-bold text-[#0A2540] leading-[1.05]">Your intelligent career assistant.</h2>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed max-w-xl">
              Explore opportunities at VS-Solutions with a portal-aware assistant. Ask about live roles, hackathons, career events, hiring process — or your own applications once you sign in.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {["Find Python jobs for freshers","Which roles match my skills?","What skills should I develop?","Show upcoming hackathons"].map(q =>
                <span key={q} className="text-xs border border-slate-200 rounded-full px-3 py-1.5 text-slate-700">{q}</span>
              )}
            </div>
            <p className="mt-6 text-xs text-slate-500 max-w-md">VS AI uses real portal data. It never invents jobs, prizes, deadlines, or expose confidential HR information.</p>
          </div>
          <div className="lg:col-span-6">
            <div className="relative rounded-3xl overflow-hidden bg-[#0A2540] p-8 md:p-10 text-white">
              <div className="absolute -right-24 -top-24 w-64 h-64 bg-[#F59E0B]/20 rounded-full blur-3xl"/>
              <div className="relative">
                <div className="w-12 h-12 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center border border-[#F59E0B]/30">
                  <Sparkles className="w-6 h-6 text-[#F59E0B]"/>
                </div>
                <div className="mt-6 font-display text-xl">"Find Python fresher jobs in Hyderabad"</div>
                <div className="mt-3 text-sm text-slate-300 leading-relaxed">VS AI · <span className="text-[#F59E0B]">Live portal data</span></div>
                <div className="mt-6 border-t border-white/10 pt-6 text-sm text-slate-300">
                  Here are the current openings I found:
                  <ul className="mt-3 space-y-2">
                    <li>• Python Developer — Fresher · Hyderabad</li>
                    <li>• Junior Python Engineer · Bengaluru</li>
                    <li>• Python Engineer · Hyderabad</li>
                  </ul>
                </div>
                <div className="mt-6 flex items-center gap-3">
                  <button onClick={() => document.querySelector('[data-testid=vs-ai-open]')?.click()} data-testid="meet-vsai-cta"
                    className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-[#0A2540] font-semibold rounded-full px-5 py-2 text-sm">Ask VS AI</button>
                  <span className="text-xs text-slate-400">Available on every page</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── TALENT COMMUNITY BIG CTA ────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{backgroundImage: "url(https://images.unsplash.com/photo-1522071901873-411886a10004?w=1600&q=85)", backgroundSize:'cover', backgroundPosition:'center'}}/>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/95 via-[#0A2540]/85 to-[#0A2540]/60"/>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="max-w-2xl text-white">
            <div className="text-xs uppercase tracking-[0.25em] text-[#F59E0B] font-semibold">Talent Community</div>
            <h2 className="mt-3 font-display text-4xl md:text-6xl font-bold leading-[1.05]">Your next opportunity may be right around the corner.</h2>
            <p className="mt-5 text-slate-300 text-lg leading-relaxed max-w-xl">Join the VS-Solutions Talent Community and stay connected with future opportunities matched to your skills and interests.</p>
            <Button asChild size="lg" className="mt-8 bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90 font-semibold" data-testid="talent-cta">
              <Link to="/register">Join Our Talent Community <ArrowRight className="w-4 h-4 ml-2"/></Link>
            </Button>
          </div>
        </div>
      </section>

      <PublicFooter />
      <VSAIWidget />
    </div>
  );
}
